import React from "react";

import Image from "next/image";
import imageUrlBuilder from "@sanity/image-url";
import { PortableText, PortableTextComponents } from "@portabletext/react";
import { merge, cloneDeep } from "lodash-es";
import { getImageDimensions } from "@sanity/asset-utils";
import { prodClient } from "./lib/client";
import { sanityImageUrl } from "@/lib/utils";
import { RichtextVideo } from "./components/RichtextVideo";

import listBullet from "@/public/global/list-bullet.svg";

const sanityImageBuilder = imageUrlBuilder(prodClient);

export const richTextComponent: PortableTextComponents = {
  marks: {
    strong: ({ children }) => <strong className="font-bold">{children}</strong>,
    greenSerifHighlight: ({ children }) => (
      <span className="font-bold text-[#6DA991] font-gestura">{children}</span>
    ),
    fontInstrument: ({ children }) => (
      <span className="font-instrument">{children}</span>
    ),
    fontGestura: ({ children }) => (
      <span className="font-gestura">{children}</span>
    ),
    underline: ({ children }) => <u className="underline">{children}</u>,
    em: ({ children }) => <em className="pr-[.125em]">{children}</em>,
  },
  block: {
    h1: ({ children }) => <h1 className="text-4xl lg:text-5xl font-bold mt-12 mb-6 first:mt-0 leading-tight">{children}</h1>,
    h2: ({ children }) => <h2 className="text-3xl lg:text-4xl font-bold mt-10 mb-5 leading-tight">{children}</h2>,
    h3: ({ children }) => <h3 className="text-2xl lg:text-3xl font-semibold mt-8 mb-4 leading-tight">{children}</h3>,
    h4: ({ children }) => <h4 className="text-xl lg:text-2xl font-semibold mt-6 mb-3">{children}</h4>,
    h5: ({ children }) => <h5 className="text-lg lg:text-xl font-medium mt-4 mb-2">{children}</h5>,
    h6: ({ children }) => <h6 className="text-base lg:text-lg font-medium mt-4 mb-2">{children}</h6>,
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-accent pl-6 pr-4 py-6 my-8 bg-primary/5 italic text-xl leading-relaxed">
        {children}
      </blockquote>
    ),
    normal: ({ children }) => (
      <p className="font-geist opacity-80 text-lg leading-relaxed mb-6">{children}</p>
    ),
    medium: ({ children }) => <p className="text-md mb-4">{children}</p>,
  },
  types: {
    image: ({ value }) => {
      return (
        <div className="lg:my-[4rem] my-[2rem] overflow-hidden w-full h-[46.5rem] rounded-[.75rem]">
          <Image
            className="!w-full !h-full !object-cover"
            src={{
              src: sanityImageBuilder.image(value).url(),
              width: getImageDimensions(value)?.width,
              height: getImageDimensions(value)?.height,
            }}
            alt={value?.alt || ""}
          />
        </div>
      );
    },

    video: ({ value }) => {
      const playbackId = value?.asset?.asset?.playbackId ||
                         value?.asset?.playbackId ||
                         value?.playbackId;

      if (!playbackId) {
        console.error('Video playbackId not found.');
        console.error('Full value structure:', JSON.stringify(value, null, 2));
        return (
          <div className="lg:my-[6rem] my-[4rem] w-full p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            <p>Erro: Vídeo não pode ser carregado. playbackId não encontrado.</p>
            <p className="text-sm mt-2">Verifique o console do navegador para mais detalhes.</p>
          </div>
        );
      }

      return (
        <RichtextVideo
          playbackId={playbackId}
          thumbnailUrl={value?.thumbnail?.src}
          description={value?.description}
        />
      );
    },

    solidLine: ({ value }) => {
      const marginClass = value?.marginSize || "my-[4rem]";

      return (
        <hr className={`w-full border-t border-black/10 ${marginClass}`} />
      );
    },
    dashedLine: ({ value }) => {
      const marginClass = value?.marginSize || "my-[3rem] lg:my-[4rem]";

      return (
        <hr
          className={`w-full border-0 h-[1px] lg:h-[2px] ${marginClass}`}
          style={{
            backgroundImage:
              "repeating-linear-gradient(to right, #10303733 0, #10303733 .75rem, transparent .75rem, transparent 1.5rem)",
          }}
        />
      );
    },
    cornerList: ({ value }) => {
      return (
        <ul className="flex flex-col items-start p-0 list-none cornerList mb-[1.25em] gap-[1rem]">
          {value?.items?.map((item: any, index: number) => {
            return (
              <li key={index} className="flex items-center p-0 m-0 gap-[1rem]">
                <div className="cornerListCorner bg-[#112628] flex-shrink-0 w-[1.5rem] h-[1.5rem] [clip-path:polygon(0_0,calc(100%-.75rem)_0,100%_.75rem,100%_100%,0_100%)]" />
                <div className="richtext richtext-start prose-p:leading-[1.1em]">
                  <PortableText
                    value={item.content}
                    components={richTextComponent}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      );
    },
    cornerChip: ({ value }) => {
      const imageUrl = value?.image?.asset?._ref
        ? sanityImageUrl(value?.image?.asset?._ref)
        : null;
      const text = value?.text || "";

      return (
        <div className="rt-cornerChip inline-flex items-center gap-[1rem] min-h-[4rem] lg:min-h-[5rem] pl-[2rem] pr-[2.5rem] prose-p:text-black bg-[#D9F9B9] [clip-path:polygon(0_0,calc(100%-1.875rem)_0,100%_1.875rem,100%_100%,0_100%)]">
          {imageUrl && (
            <Image
              className="!w-[2rem] !h-auto !m-0"
              src={imageUrl}
              width={24}
              height={24}
              alt=""
            />
          )}
          <p className="mb-0 leading-none font-geistmono text-[2rem] translate-y-[.125rem]">
            {text}
          </p>
        </div>
      );
    },
    numberedList: ({ value }) => {
      const items = value?.items || [];
      const startNumber = value?.startNumber || 1;

      return (
        <ol
          className="flex flex-col items-start p-0 list-none numberedList mb-[1.25rem] gap-[1rem]"
          start={startNumber}
        >
          {items.map((item: any, index: number) => {
            return (
              <li
                key={index}
                className="flex items-center p-0 m-0 gap-[.625rem]"
              >
                <span className="flex-shrink-0 text-[#D9F9B9] font-geist min-w-[2rem]">
                  {startNumber + index}.
                </span>

                <div className="richtext richtext-start">
                  <PortableText
                    value={item.content}
                    components={richTextComponent}
                  />
                </div>
              </li>
            );
          })}
        </ol>
      );
    },
    quote: ({ value }) => {
      const name = value?.name || "";
      const text = value?.text || [];

      return (
        <div className="w-full p-[4rem] lg:p-[5.75rem] my-[4.5rem] bg-[#CAA62B] flex flex-col gap-[3rem] rounded-[.75rem]">
          <div className="flex items-center gap-[3rem]">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="31"
              viewBox="0 0 32 31"
              fill="none"
            >
              <path
                d="M13.1455 0.5L12.9541 1.14258L8.13867 17.3174H13.2881V30.5H0.5V19.2441L0.526367 19.166L6.78613 0.838867L6.90137 0.5H13.1455ZM30.3574 0.5L30.166 1.14258L25.3506 17.3174H30.5V30.5H17.7119V19.2441L17.7393 19.166L23.998 0.838867L24.1133 0.5H30.3574Z"
                fill="white"
                stroke="white"
              />
            </svg>

            <p className="block font-geist text-white/80 text-base">
              {name}
            </p>
          </div>

          <PortableText
            value={text}
            components={customRichTextComponent({
              block: {
                normal: ({ children }) => (
                  <p className="text-[3rem] leading-[4.2rem] text-white">
                    {children}
                  </p>
                ),
              },
            })}
          />
        </div>
      );
    },
    box: ({ value }) => {
      const text = value?.text || [];

      return (
        <div className="w-full border-l border-[#F4411C] pl-[3.75rem] my-[4.5rem] flex flex-col gap-[3rem]">
          <PortableText value={text} components={richTextComponent} />
        </div>
      );
    },
    dividerTextGroup: ({ value }) => {
      const title = value?.title || [];
      const texts = value?.texts || [];

      return (
        <div className="w-full my-[4.5rem] flex gap-[3.75rem]">
          <div className="self-stretch w-[.125rem] bg-accent"></div>
          <div className="flex flex-col gap-[3rem]  text-black">
            <PortableText value={title} components={richTextComponent} />

            <ul>
              {texts?.map((item: string, index: number) => {
                return (
                  <li
                    className="mb-[3rem] last:mb-0 last:pb-0  pb-[3rem] border-b-[.125rem] border-black/[0.10] last:border-b-0"
                    key={index}
                  >
                    <p className="font-geist text-black/80 font-medium leading-[180%]">
                      {item}
                    </p>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      );
    },
    iconItem: ({ value }) => {
      const iconUrl = value?.icon?.asset?._ref
        ? sanityImageUrl(value?.icon?.asset?._ref)
        : null;
      const title = value?.title || "";

      return (
        <div className="flex flex-col items-start iconItem gap-[1rem] mb-[1rem]">
          <div className="flex items-center gap-[2.25rem]">
            {iconUrl && (
              <Image
                className="!w-[5rem] !h-[5rem] !m-0 flex-shrink-0"
                src={iconUrl}
                width={32}
                height={32}
                alt=""
              />
            )}
            {title && (
              <p className="m-0 text-[#1C1F22] text-[2.5rem]">{title}</p>
            )}
          </div>
          <div className="flex-1">
            <div className="richtext richtext-start prose-p:text-[2.25rem] prose-strong:font-[600]">
              <PortableText
                value={value?.text}
                components={richTextComponent}
              />
            </div>
          </div>
        </div>
      );
    },
    iconItemSimple: ({ value }) => {
      return (
        <div className="flex items-center gap-[2rem] lg:gap-[3rem] mb-[1rem] py-[2rem] lg:py-[3rem] border-b border-b-black/5 last:border-b-0">
          <Image
            className="!w-[2.5rem] !min-w-[2.5rem]"
            src={listBullet}
            alt="bullet list"
          />

          <div className="richtext richtext-start prose-p:text-[2rem] prose-strong:font-[600]">
            <PortableText value={value?.text} components={richTextComponent} />
          </div>
        </div>
      );
    },
    arrowBullet: ({ value }) => {
      const imageUrl = value?.image?.asset?._ref
        ? sanityImageUrl(value?.image?.asset?._ref)
        : null;

      return (
        <div className="flex items-start arrowBullet gap-[1rem] mb-[1rem]">
          {imageUrl && (
            <Image
              className="!w-[3.25rem] !h-[3.25rem] !m-0 flex-shrink-0"
              src={imageUrl}
              width={32}
              height={32}
              alt=""
            />
          )}
          <div className="flex-1 richtext richtext-start prose-p:text-[2.25rem] prose-strong:font-[600]">
            <PortableText value={value?.text} components={richTextComponent} />
          </div>
        </div>
      );
    },
  },
  list: {
    bullet: ({ children }) => (
      <ul className="my-[4.5rem] p-[2.35rem] lg:p-[4.75rem] text-black/80">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="my-4 space-y-4 text-black/70">{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => (
      <li className="flex flex-row items-center gap-[3rem] leading-[3rem] pb-[3rem] mb-[3rem] border-b-[.125rem] border-white/[0.10] last:pb-0 last:mb-0 last:border-b-0">
        <span className="w-[1rem] min-w-[1rem] min-h-[1rem] h-[1rem] bg-accent"></span>
        <span>{children}</span>
      </li>
    ),
    number: ({ children, index }) => (
      <li className="flex flex-row items-center gap-[3rem] leading-[3rem] pb-[3rem] mb-[3rem] border-b-[.125rem] border-white/[0.10] last:pb-0 last:mb-0 last:border-b-0">
        <span className="w-[1rem] min-w-[1rem] min-h-[1rem] h-[1rem] bg-accent"></span>
        <span className="font-geist text-accent ">
          {String((index ?? 0) + 1).padStart(2, "0")}
        </span>
        <span>{children}</span>
      </li>
    ),
  },
};

export const customRichTextComponent = (props: PortableTextComponents) => {
  return merge(cloneDeep(richTextComponent), props);
};
